import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import GridLayout, { Layout, WidthProvider } from "react-grid-layout";

import User from "@mapmycustomers/shared/types/User";

import {
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  GRID_WIDTH,
} from "@app/scene/dashboard/components/DashboardContent/util/consts";
import { GridCardInfo } from "@app/scene/dashboard/components/DashboardContent/util/useGridCards";
import dragStyles from "@app/scene/dashboard/components/styles/common.module.scss";
import { saveCard, saveDashboardLayout } from "@app/scene/dashboard/store/actions";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import "@app/styles/react-grid.scss";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";

import GridCard from "./components/GridCard";
import styles from "./Grid.module.scss";

const ReactGridLayout = WidthProvider(GridLayout);

const CONTAINER_PADDING = GRID_MARGIN as [number, number];

// prevent selecting text on page when dragging cards
const handleDragStart = () => (document.body.style.userSelect = "none");
const handleDragEnd = () => (document.body.style.userSelect = "auto");

interface GridProps {
  cards: GridCardInfo[];
  draggable?: boolean;
  onCopy?: (cardId: DashboardCard["id"]) => void;
  onEdit?: (cardId: DashboardCard["id"]) => void;
  onRemove?: (cardId: DashboardCard["id"]) => void;
  scope: Scope;
}

interface Props extends GridProps {
  currentUserId: User["id"];
  onSaveCard: (payload: {
    cardId: DashboardCard["id"];
    configuration: CardConfiguration<any, any, any>;
    fullWidth?: DashboardCard["fullWidth"];
    scope: Scope;
    updateGoalOverrides: boolean;
    updatePreferencesOverrides: boolean;
  }) => void;
  onSaveLayout: (payload: {
    cardPositions: Record<DashboardCard["id"], Pick<DashboardCard, "x" | "y">>;
    scope: Scope;
  }) => void;
}

const Grid: React.FC<Props> = ({
  cards,
  currentUserId,
  draggable,
  onCopy,
  onEdit,
  onRemove,
  onSaveCard,
  onSaveLayout,
  scope,
}) => {
  const [boardLayout, setBoardLayout] = useState<Layout[]>([]);
  useEffect(() => {
    setBoardLayout(cards.map(({ layout }) => layout));
  }, [cards]);

  const handleLayoutChange = useCallback(
    (layout: Layout[]) => {
      if (!draggable) {
        // disallow changing layout when it's not draggable
        return;
      }

      if (layout.every((item) => item.w === 1 && item.h === 1 && item.x === 0)) {
        // sometimes ReactGridLayout calls onLayoutChange with these weird values
        // like it resets cards for an unknown (yet) reasons
        // I found no way to prevent that, but at least we can try detecting such weird change
        // with this condition. If all cards are 1x1 and their x is 0 (and y increments by 1),
        // we know this is a failed repositioning and ignore it.
        return;
      }

      setBoardLayout((prevLayout) => {
        if (!prevLayout.length) {
          // This condition is needed to avoid layout changes when boardLayout var is still [], but
          // GridLayout is already initialized and triggers initial onLayoutChange callback.
          // In that callback it provides kinda a default layout according to children it found.
          // We wanna ignore that, because just a few moment after useEffect will run and provide
          // our own boardLayout.
          return prevLayout;
        }

        let updatedAnyPositions = false;
        const layoutByCardId = new Map(layout.map((layout) => [layout.i, layout]));
        const updatedCardPositions = cards.reduce((result, card) => {
          const newLayout = layoutByCardId.get(String(card.id));
          if (newLayout && (card.layout.x !== newLayout?.x || card.layout.y !== newLayout?.y)) {
            updatedAnyPositions = true;
          }
          result[String(card.id)] = {
            x: newLayout?.x ?? card.layout.x,
            y: newLayout?.y ?? card.layout.y,
          };
          return result;
        }, {} as Record<DashboardCard["id"], Pick<DashboardCard, "x" | "y">>);

        if (updatedAnyPositions) {
          onSaveLayout?.({ cardPositions: updatedCardPositions, scope });
          return layout;
        } else {
          return prevLayout;
        }
      });
    },
    [draggable, onSaveLayout, scope, cards]
  );

  return (
    <ReactGridLayout
      autoSize
      className={styles.container}
      cols={GRID_WIDTH}
      compactType="vertical"
      containerPadding={CONTAINER_PADDING}
      draggableCancel={`.${dragStyles.nonDraggable}`}
      draggableHandle={`.${dragStyles.draggable}`}
      isDraggable
      layout={boardLayout}
      margin={GRID_MARGIN}
      onDragStart={handleDragStart}
      onDragStop={handleDragEnd}
      onLayoutChange={handleLayoutChange}
      rowHeight={GRID_ROW_HEIGHT}
    >
      {cards.map((card) => (
        <GridCard
          configuration={card.configuration}
          currentUserId={currentUserId}
          draggable={draggable}
          id={card.id}
          key={card.layout.i}
          onCopy={onCopy}
          onEdit={onEdit}
          onRemove={onRemove}
          onSaveCard={onSaveCard}
          scope={scope}
          type={card.type}
          viewState={card.viewState}
        />
      ))}
    </ReactGridLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
});

const mapDispatchToProps = {
  onSaveCard: saveCard.request,
  onSaveLayout: saveDashboardLayout.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Grid);
